import "./i18n";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SolanaWalletProvider } from "./context/WalletContext";
import { SiteProvider } from "./context/SiteContext";
import { TokenProvider } from "./context/TokenContext";
import { WalletMismatchGuard } from "./components/WalletMismatchGuard";
import OnboardingTour from "./components/OnboardingTour";
import { LoginModalProvider, useLoginModal } from "./components/LoginModal";
import { Layout } from "./components/Layout";
import { useState, useEffect } from "react";
import axios from "axios";

// Pages
import { Landing } from "./pages/Landing";
import { Login } from "./pages/LoginWallet";
import { Home } from "./pages/Home";
import About from "./pages/About";
import { Pools } from "./pages/Pools";
import { PoolDetail } from "./pages/PoolDetail";
import { CategoryDetail } from "./pages/CategoryDetail";
import { SearchPage } from "./pages/SearchPage";
import { Inventory } from "./pages/Inventory";
import { Wallet } from "./pages/Wallet";
import { Profile } from "./pages/Profile";
import { Shop } from "./pages/Shop";
import { Staking } from "./pages/Staking";
import { Support } from "./pages/Support";
import { Admin } from "./pages/Admin";
import { AdminLogin } from "./pages/AdminLogin";
import { AdminPageEditor } from "./pages/AdminPageEditor";
import { CustomPage } from "./pages/CustomPage";
import SwapPage from "./pages/SwapPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { openLogin } = useLoginModal();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      openLogin();
    }
  }, [loading, isAuthenticated, openLogin]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF7] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Login Redirect - /login now opens the global modal instead of a page
const LoginRedirect = () => {
  const { isAuthenticated } = useAuth();
  const { openLogin } = useLoginModal();

  useEffect(() => {
    if (!isAuthenticated) {
      openLogin();
    }
  }, [isAuthenticated, openLogin]);

  return <Navigate to={isAuthenticated ? "/home" : "/"} replace />;
};

// Admin Route - Check if authenticated AND admin
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF7] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  // If not authenticated, redirect to admin login
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  // If authenticated but not admin, redirect to home
  if (!isAdmin) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF7] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Admin Routes - No Layout (keep dark theme) */}
      <Route path="/admin/login" element={<div className="admin-shell"><AdminLogin /></div>} />
      <Route path="/admin" element={<AdminRoute><div className="admin-shell"><Admin /></div></AdminRoute>} />
      <Route path="/admin/pages/new" element={<AdminRoute><div className="admin-shell"><AdminPageEditor /></div></AdminRoute>} />
      <Route path="/admin/pages/:pageId" element={<AdminRoute><div className="admin-shell"><AdminPageEditor /></div></AdminRoute>} />
      
      {/* All other routes with Layout */}
      <Route path="/*" element={
        <Layout>
          <Routes>
            {/* Public Routes - Can browse without login */}
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Home />} />
            <Route path="/pools" element={<Pools />} />
            <Route path="/pool/:poolId" element={<PoolDetail />} />
            <Route path="/category/:slug" element={<CategoryDetail />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/about" element={<About />} />
            
            {/* Auth Route - opens global login modal instead of a page */}
            <Route path="/login" element={<LoginRedirect />} />
            
            {/* Inventory - public, shows Connect Wallet prompt when not logged in */}
            <Route path="/inventory" element={<Inventory />} />
            {/* Market/Shop - public, buy/sell requires Connect Wallet */}
            <Route path="/shop" element={<Shop />} />
            {/* Protected Routes - Need login */}
            <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
            <Route path="/staking" element={<ProtectedRoute><Staking /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
            <Route path="/swap" element={<ProtectedRoute><SwapPage /></ProtectedRoute>} />
            
            {/* Custom Pages - Must be BEFORE fallback, but after specific routes */}
            <Route path="/:slug" element={<CustomPage />} />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      } />
    </Routes>
  );
}

// Inner wrapper that has access to AuthContext for wallet disconnect handling
function WalletAuthWrapper({ solanaSettings, children }) {
  const { logout } = useAuth();
  
  return (
    <SolanaWalletProvider 
      rpcUrl={solanaSettings.rpc_url} 
      network={solanaSettings.network}
      onWalletDisconnect={logout}
    >
      {children}
    </SolanaWalletProvider>
  );
}

function App() {
  const [solanaSettings, setSolanaSettings] = useState({ rpc_url: null, network: 'mainnet-beta' });

  useEffect(() => {
    // Fetch Solana settings on app load
    axios.get(`${API}/solana/settings`)
      .then(res => setSolanaSettings(res.data))
      .catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <SiteProvider>
        <TokenProvider>
          <AuthProvider>
            <WalletAuthWrapper solanaSettings={solanaSettings}>
              <LoginModalProvider>
                <WalletMismatchGuard>
                  <OnboardingTour />
                  <AppRoutes />
                </WalletMismatchGuard>
              </LoginModalProvider>
              <Toaster 
                position="top-center" 
                toastOptions={{
                  style: {
                    background: 'var(--color-card, #18181b)',
                    border: '1px solid var(--color-card-border, #27272a)',
                    color: 'var(--color-text-primary, #fafafa)'
                  }
                }}
              />
            </WalletAuthWrapper>
          </AuthProvider>
        </TokenProvider>
      </SiteProvider>
    </BrowserRouter>
  );
}

export default App;
